/**
 * useGuardianships / useGuardianship / useCreateGuardianship / useRevokeGuardianship
 * — Brief 5 B6 Guardianship / Curadoria (Sprint Frontend Brief 5 · Tela 2).
 *
 * Consome o módulo adversarial (guardianships.controller):
 *   - GET    /v1/guardianships              → lista (tenant-scoped)
 *   - GET    /v1/guardianships/:id          → detalhe (drawer, #315)
 *   - POST   /v1/guardianships              → criar (audit guardianship.granted)
 *   - DELETE /v1/guardianships/:id (If-Match) → revogar (audit guardianship.revoked)
 *
 * Gate server-side: `legal-capacity-management` (Regulated). Revoke usa
 * optimistic-lock por `version` (If-Match, ADR-071) — mesmo padrão dos
 * conflict-alerts. Mutations invalidam a lista canonicamente.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  CreateGuardianshipRequest,
  GuardianshipWireItem,
} from '../features/dpo/guardianship/types';

const QUERY_KEY = 'guardianships';
const LIST_STALE_TIME = 30_000;

export function useGuardianships(enabled = true): {
  data: GuardianshipWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => api.listGuardianships(),
    staleTime: LIST_STALE_TIME,
    retry: 1,
    enabled,
  });
  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useGuardianship(guardianshipId: string | null): {
  data: GuardianshipWireItem | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY, 'detail', guardianshipId],
    queryFn: () => api.getGuardianship(guardianshipId!),
    enabled: guardianshipId !== null,
    retry: 1,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useCreateGuardianship(): ReturnType<
  typeof useMutation<GuardianshipWireItem, Error, CreateGuardianshipRequest>
> {
  const qc = useQueryClient();
  return useMutation<GuardianshipWireItem, Error, CreateGuardianshipRequest>({
    mutationFn: (body) => api.createGuardianship(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

interface RevokeVars {
  id: string;
  version: number;
  reason: string;
}

export function useRevokeGuardianship(): ReturnType<
  typeof useMutation<GuardianshipWireItem, Error, RevokeVars>
> {
  const qc = useQueryClient();
  return useMutation<GuardianshipWireItem, Error, RevokeVars>({
    mutationFn: ({ id, version, reason }) => api.revokeGuardianship(id, version, { reason }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      void qc.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', vars.id] });
    },
  });
}
